import React from 'react';
import { SquarePlus, Lightbulb, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCommunityAppRequests } from '@/hooks/community/use-community-app-requests';
import CompactAppRequestCard from './CompactAppRequestCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface CommunityAppRequestsContainerProps {
  onAppIdeaClick?: (app: any) => void;
  onViewAllAppRequests?: () => void;
}

const CommunityAppRequestsContainer: React.FC<CommunityAppRequestsContainerProps> = ({
  onAppIdeaClick,
  onViewAllAppRequests
}) => {
  const { 
    topAppRequests, 
    isLoading, 
    hasError, 
    handleToggleAppRequestFavorite,
    totalAppRequests,
    isEmpty
  } = useCommunityAppRequests();

  if (isLoading) {
    return (
      <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="text-purple-500 dark:text-purple-400" size={18} />
          <h3 className="font-medium text-base">Top App Requests</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-3 rounded-lg bg-white/40 dark:bg-white/5 border border-white/20 dark:border-white/10 animate-pulse">
              <div className="flex items-start gap-3">
                <Skeleton className="w-8 h-8 rounded-lg bg-slate-300 dark:bg-slate-600" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="w-3/4 h-4 bg-slate-300 dark:bg-slate-600" />
                  <Skeleton className="w-full h-3 bg-slate-300 dark:bg-slate-600" />
                  <Skeleton className="w-1/2 h-2 bg-slate-300 dark:bg-slate-600" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="text-purple-500 dark:text-purple-400" size={18} />
          <h3 className="font-medium text-base">Top App Requests</h3>
        </div>
        <div className="text-center py-4">
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load app requests</p>
          <p className="text-xs text-muted-foreground mt-1">Please try again later</p>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        
        {/* Top border highlight */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-300/30 to-transparent dark:via-purple-500/20 rounded-t-lg" />
        
        {/* Glass effect */}
        <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />

        {/* Section Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="text-purple-500 dark:text-purple-400" size={18} />
            <h3 className="font-medium text-base">App Requests</h3>
          </div>
        </div>

        {/* Empty State */}
        <div className="text-center py-6">
          <motion.div
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 mb-3"
          >
            <SquarePlus className="h-6 w-6 text-white" />
          </motion.div>
          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
            No App Requests Yet
          </h4>
          <p className="text-xs text-muted-foreground mb-4">
            Be the first to request an app for the community!
          </p>
          

        </div>
      </div>
    );
  }

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-300/30 to-transparent dark:via-purple-500/20 rounded-t-lg" />
      
      {/* Glass effect */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />

      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="text-purple-500 dark:text-purple-400" size={18} />
          <h3 className="font-medium text-base">Top App Requests</h3>
        </div>
        
        {totalAppRequests > 5 && onViewAllAppRequests && (
          <button
            onClick={onViewAllAppRequests}
            className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            <span>View All</span>
            <ArrowRight size={12} />
          </button>
        )}
      </div>

      {/* App Requests List */}
      <div className="space-y-3">
        {topAppRequests.map((app, index) => (
          <motion.div
            key={app.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.1 }}
          >
            <CompactAppRequestCard
              app={app}
              isFavorited={app.isFavorited}
              onToggleFavorite={handleToggleAppRequestFavorite}
              onAppIdeaClick={onAppIdeaClick || (() => {})}
            />
          </motion.div>
        ))}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-end mt-4 pt-3 border-t border-white/20 dark:border-white/10">
        <span className="text-xs text-muted-foreground">
          {totalAppRequests} total request{totalAppRequests !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default CommunityAppRequestsContainer; 